import type { GeminiEnrichmentSettings, GeminiImageSettings } from './types'

export const ENRICHMENT_PROMPT_TEMPLATE_KEYS = ['character', 'background'] as const
export type EnrichmentPromptTemplateKey = (typeof ENRICHMENT_PROMPT_TEMPLATE_KEYS)[number]

export const ENRICHMENT_PROMPT_INPUT_MARKER = '[USER_INPUT]'
export const ENRICHMENT_PROMPT_TEMPLATE_MAX_LENGTH = 50_000

export const ENRICHMENT_PROMPT_TEMPLATE_ATTRIBUTES = {
  character: 'characterEnrichmentPromptTemplate',
  background: 'backgroundEnrichmentPromptTemplate'
} as const

export type EnrichmentPromptTemplates = Record<EnrichmentPromptTemplateKey, string>
export type EnrichmentPromptTemplateSource = 'code' | 'character'
export type EnrichmentPromptTemplatePatch = Partial<Record<EnrichmentPromptTemplateKey, string | null>>

export interface EnrichmentPromptTemplateSettings {
  characterEnrichmentPromptTemplate?: string
  backgroundEnrichmentPromptTemplate?: string
}

export interface EnrichmentPromptTemplateConfig {
  promptTemplates: EnrichmentPromptTemplates
  promptTemplateCodeDefaults: EnrichmentPromptTemplates
  promptTemplateSources: Record<EnrichmentPromptTemplateKey, EnrichmentPromptTemplateSource>
}

/** Only supplied sections change. Null removes a prompt override; an empty URL clears the link. */
export interface SaveAdminGeminiSettingsRequest {
  imageSettings?: GeminiImageSettings
  enrichmentSettings?: GeminiEnrichmentSettings
  guidelinesUrl?: string
  promptTemplates?: EnrichmentPromptTemplatePatch
}

/** Does not normalize the template: line breaks and spacing are intentional. */
export function getEnrichmentPromptTemplateError(template: unknown): string | undefined {
  if (typeof template !== 'string' || template.trim().length === 0) {
    return 'Enter an enrichment prompt containing [USER_INPUT] exactly once.'
  }
  if (template.length > ENRICHMENT_PROMPT_TEMPLATE_MAX_LENGTH) {
    return 'Enrichment prompts must be 50,000 characters or fewer.'
  }
  const markerCount = template.split(ENRICHMENT_PROMPT_INPUT_MARKER).length - 1
  if (markerCount === 0) {
    return 'Include the exact [USER_INPUT] marker once so the generation input can be inserted.'
  }
  if (markerCount > 1) {
    return 'Keep [USER_INPUT] exactly once. Remove the duplicate markers.'
  }
  return undefined
}

/** A callback prevents JavaScript replacement patterns in input (e.g. $&) from being interpreted. */
export function injectEnrichmentPromptInput(template: string, input: string): string {
  return template.replace(ENRICHMENT_PROMPT_INPUT_MARKER, () => input)
}
